/**
 * features/tax-invoices/TaxInvoiceFormPage.tsx — สร้าง/แก้ไขใบเสร็จ/ใบกำกับภาษีโดยตรง
 *
 * ปกติใบกำกับภาษีจะออกผ่านปุ่ม "ออกใบกำกับภาษี" จากใบเสนอราคา
 * หน้านี้สำหรับกรณีที่ต้องออกตรงๆ โดยไม่มีใบเสนอราคาอ้างอิง (เช่น ขายหน้าร้าน)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { taxInvoiceInputSchema, type TaxInvoiceInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import { useCompany } from '~/features/company/hooks';
import { PageHeader } from '~/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '~/ui/primitives';
import { useToast } from '~/ui/overlays';
import { CustomerPicker } from '~/features/documents/CustomerPicker';
import { DocumentItemsEditor } from '~/features/documents/DocumentItemsEditor';
import { DocumentSummaryPanel } from '~/features/documents/DocumentSummaryPanel';
import { EMPTY_DOCUMENT_FORM, type DocumentFormState } from '~/features/documents/types';
import { useCreateTaxInvoice, useTaxInvoice, useUpdateTaxInvoice } from './hooks';

export function TaxInvoiceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id) && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const { data: existing, isLoading: loadingExisting } = useTaxInvoice(isEdit ? id : undefined);
  const { data: company } = useCompany();

  const [form, setForm] = useState<DocumentFormState>(EMPTY_DOCUMENT_FORM);
  const [itemErrors, setItemErrors] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useCreateTaxInvoice();
  const update = useUpdateTaxInvoice();
  const saving = create.isPending || update.isPending;

  useEffect(() => {
    if (!existing) return;
    setForm({
      date: existing.date ?? new Date().toISOString().slice(0, 10),
      customerId: existing.customerId,
      customerName: existing.customerName,
      customerAddress: existing.customerAddress,
      customerTaxId: existing.customerTaxId,
      customerBranch: existing.customerBranch,
      customerPhone: existing.customerPhone,
      discountAmount: existing.discountAmount,
      notes: existing.notes,
      items: existing.items.map((item) => ({
        productName: item.productName,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice,
        discount: item.discount,
      })),
    });
  }, [existing]);

  useEffect(() => {
    if (isEdit && existing?.status === 'cancelled') {
      toast.error('ใบกำกับภาษีที่ยกเลิกแล้วไม่สามารถแก้ไขได้');
      navigate(`/tax-invoices/${id}`, { replace: true });
    }
  }, [isEdit, existing, id, navigate, toast]);

  function patchForm(patch: Partial<DocumentFormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  async function handleSubmit() {
    setFormError(null);
    setItemErrors([]);

    const input: TaxInvoiceInput = {
      date: new Date(form.date),
      customerId: form.customerId,
      customerName: form.customerName,
      customerAddress: form.customerAddress,
      customerTaxId: form.customerTaxId,
      customerBranch: form.customerBranch,
      customerPhone: form.customerPhone,
      discountAmount: form.discountAmount,
      notes: form.notes,
      status: existing?.status ?? 'issued',
      sourceType: existing?.sourceType === 'quotation' ? 'quotation' : null,
      sourceId: existing?.sourceId || null,
      items: form.items,
    };

    const parsed = taxInvoiceInputSchema.safeParse(input);
    if (!parsed.success) {
      const itemIssues: string[] = [];
      let topError: string | null = null;

      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'items' && typeof issue.path[1] === 'number') {
          itemIssues[issue.path[1]] = issue.message;
        } else if (!topError) {
          topError = issue.message;
        }
      }

      setItemErrors(itemIssues);
      setFormError(topError ?? 'กรุณาตรวจสอบข้อมูลในฟอร์ม');
      return;
    }

    try {
      if (isEdit && id) {
        await update.mutateAsync({ id, input: parsed.data });
        toast.success('บันทึกใบกำกับภาษีแล้ว');
        navigate(`/tax-invoices/${id}`);
      } else {
        const created = await create.mutateAsync(parsed.data);
        toast.success(`สร้างใบกำกับภาษี ${created.documentNumber} แล้ว`);
        navigate(`/tax-invoices/${created.id}`);
      }
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ กรุณาลองใหม่');
    }
  }

  if (isEdit && loadingExisting) {
    return <p className="text-sm text-fg-muted">กำลังโหลด…</p>;
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? `แก้ไขใบกำกับภาษี ${existing?.documentNumber ?? ''}` : 'สร้างใบเสร็จ/ใบกำกับภาษี'}
        actions={
          <Button variant="ghost" onClick={() => navigate('/tax-invoices')}>
            <ArrowLeft size={15} />
            กลับ
          </Button>
        }
      />

      {formError ? (
        <div className="mb-4">
          <Alert tone="danger">{formError}</Alert>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">ข้อมูลเอกสาร</h3>
            <Field label="วันที่เอกสาร" required>
              <Input
                type="date"
                value={form.date}
                onChange={(event) => patchForm({ date: event.target.value })}
              />
            </Field>
          </div>

          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">ข้อมูลลูกค้า</h3>
            <CustomerPicker form={form} onChange={patchForm} />
          </div>

          <div className="card p-4">
            <h3 className="mb-3 text-sm font-semibold text-fg">รายการสินค้า/บริการ</h3>
            <DocumentItemsEditor
              items={form.items}
              onChange={(items) => patchForm({ items })}
              errors={itemErrors}
            />
          </div>

          <div className="card p-4">
            <Field label="หมายเหตุ">
              <Textarea
                value={form.notes}
                onChange={(event) => patchForm({ notes: event.target.value })}
              />
            </Field>
          </div>
        </div>

        <DocumentSummaryPanel
          form={form}
          onDiscountChange={(value) => patchForm({ discountAmount: value })}
          vatEnabled={company?.vatEnabled ?? true}
          submitSlot={
            <Button variant="primary" onClick={() => void handleSubmit()} loading={saving} className="w-full">
              <Save size={15} />
              บันทึกใบกำกับภาษี
            </Button>
          }
        />
      </div>
    </div>
  );
}
